/**
 * API e stato server del briefing giornaliero di portafoglio (SPEC-013 §5.1).
 *
 * ⚠️ Prezzi obiettivo e percentuali sono STRINGHE decimali (ADR-0006): si
 * formattano con `lib/format.ts` e non si convertono in `number`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type BriefingRating = 'Buy' | 'Overweight' | 'Hold' | 'Underweight' | 'Sell';

export type BriefingItem = {
  id: string;
  instrumentId: string | null;
  symbol: string;
  isin: string | null;
  name: string;
  inPortfolio: boolean;
  rating: BriefingRating;
  priceTarget: string | null;
  priceTargetCurrency: string | null;
  /** `true` = dall'analisi di dettaglio (autorevole), `false` = stima del digest. */
  priceTargetFromAnalysis: boolean;
  recommendedAllocationPct: string | null;
  note: string;
};

export type BriefingPick = { item: BriefingItem; note: string } | null;

export type BriefingRevision = {
  revision: number;
  status: string;
  generatedAt: string | null;
};

export type BriefingDetail = {
  id: string;
  briefingDate: string;
  generatedAt: string | null;
  status: 'collecting' | 'summarizing' | 'completed' | 'failed';
  errorMessage: string | null;
  /** Vincoli di §2.5 non rispettati dal modello: la UI lo dichiara. */
  needsReview: boolean;
  summary: string | null;
  items: BriefingItem[];
  bestPick: BriefingPick;
  worstPick: BriefingPick;
  newPick: BriefingPick;
  llmModel: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  /** Quale revisione di quel giorno si sta guardando (patch 0044). */
  revision: number;
  /** Tutte le revisioni di quel giorno, dalla più recente. */
  revisions: BriefingRevision[];
};

export type BriefingSummary = {
  id: string;
  briefingDate: string;
  status: string;
  needsReview: boolean;
  itemCount: number;
  bestSymbol: string | null;
  worstSymbol: string | null;
  newSymbol: string | null;
};

export const briefingKeys = {
  all: ['briefings'] as const,
  latest: ['briefings', 'latest'] as const,
  byDate: (date: string) => ['briefings', date] as const,
};

export function useLatestBriefing() {
  return useQuery({
    queryKey: briefingKeys.latest,
    queryFn: ({ signal }) => api.get<{ briefing: BriefingDetail | null }>('/briefings/latest', signal),
  });
}

export type NextBriefingRun = {
  /** La seduta in corso ha gia' il suo digest: il prossimo e' all'apertura dopo. */
  sessionDone: boolean;
  /** Da quando la generazione diventa possibile. `null` = non ci sara'. */
  at: string | null;
  /** Il mercato la cui apertura apre il cancello, quando c'e' un cancello. */
  waitingFor: { mic: string; name: string } | null;
  blocked: 'feature_disabled' | 'no_trigger' | null;
  /** Granularita' del poller: l'istante e' «da qui, entro un giro». */
  pollMinutes: number;
};

/**
 * Quando il digest si generera' da solo (F-111).
 *
 * `staleTime` generoso: la risposta cambia al massimo una volta al giorno — a
 * mezzanotte UTC o quando il digest di oggi nasce — e re-interrogarla a ogni
 * messa a fuoco della finestra sarebbe traffico per un dato che sta fermo.
 */
export function useNextBriefingRun() {
  return useQuery({
    queryKey: [...briefingKeys.all, 'next-run'] as const,
    queryFn: ({ signal }) => api.get<NextBriefingRun>('/briefings/next-run', signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBriefingHistory() {
  return useQuery({
    queryKey: [...briefingKeys.all, 'history'] as const,
    queryFn: ({ signal }) => api.get<{ items: BriefingSummary[]; total: number }>('/briefings', signal),
  });
}

/**
 * Il digest di un giorno. `revision` assente significa «quello di quel giorno»,
 * cioe' la revisione piu' alta; presente, chiede quel documento preciso — ed e'
 * il caso di chi arriva dalla riconciliazione di un'operazione, che cita la
 * revisione da cui la decisione e' nata.
 *
 * La revisione entra nella **chiave della query**: senza, due revisioni dello
 * stesso giorno condividerebbero la cache e la seconda mostrerebbe la prima.
 */
export function useBriefingByDate(date: string | null, revision?: number | null) {
  return useQuery({
    queryKey: [...briefingKeys.byDate(date ?? ''), revision ?? 'latest'],
    queryFn: ({ signal }) =>
      api.get<BriefingDetail>(
        revision ? `/briefings/${date}?revision=${revision}` : `/briefings/${date}`,
        signal,
      ),
    enabled: date !== null,
  });
}

/** Generazione manuale: una sola chiamata LLM, sincrona lato server. */
export type GenerateBriefingResult = BriefingDetail & {
  autoTradingRequested: boolean;
  autoTradingScheduled: boolean;
  /** Codice strutturato quando la spunta non è stata onorata (SPEC-014.2 §A.3). */
  autoTradingReason: string | null;
};

export function useGenerateBriefing() {
  const queryClient = useQueryClient();
  return useMutation<GenerateBriefingResult, Error, boolean>({
    // Vedi SPEC-014.2 §A.3: assente ⇒ nessun auto-trading. Dalla F-057 la
    // generazione manuale NON innesca più la valutazione da sola — prima lo
    // faceva, e un ordine poteva nascere da un pulsante che dice «Genera adesso».
    mutationFn: (runAutoTrading: boolean) =>
      api.post<GenerateBriefingResult>('/briefings/generate', { runAutoTrading }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: briefingKeys.all }),
    // Anche in errore si rilegge, ed è il caso del 504 del proxy: la richiesta
    // HTTP è morta, la generazione no — dopo qualche decina di secondi il
    // briefing c'è. Non è un RETRY della generazione, che spenderebbe una
    // seconda volta ~90 secondi di modello per rifare un lavoro probabilmente
    // già concluso: è una riletta del risultato.
    onError: () => void queryClient.invalidateQueries({ queryKey: briefingKeys.all }),
  });
}

// ------------------------------------------------------------------ Debug

export type BriefingDebug = {
  briefingDate: string;
  revision: number;
  status: string;
  generatedAt: string | null;
  llmModel: string | null;
  promptTokens: number | null;
  completionTokens: number | null;
  /** `null` = digest anteriore alla registrazione della diagnostica. */
  systemPrompt: string | null;
  userPrompt: string | null;
  rawResponse: string | null;
  items: Array<{ symbol: string | null; name: string; rating: string | null; inPortfolio: boolean }>;
};

/** Endpoint separato: prompt e risposta grezza sono decine di KB, e la pagina
 *  normale li trascinerebbe a ogni apertura per non mostrarli quasi mai. */
export function useBriefingDebug(date: string | null, revision?: number | null) {
  return useQuery({
    queryKey: ['briefing', 'debug', date ?? '', revision ?? 'latest'],
    queryFn: ({ signal }) =>
      api.get<BriefingDebug>(
        revision ? `/briefings/${date}/debug?revision=${revision}` : `/briefings/${date}/debug`,
        signal,
      ),
    enabled: date !== null,
  });
}

// ------------------------------------------- Storico per titolo (F-135)

export type BriefingHistoryPoint = {
  briefingDate: string;
  briefingId: string;
  revision: number;
  /** >1 = il giudizio del mattino è stato rivisto in giornata. */
  revisionCount: number;
  generatedAt: string | null;
  rating: BriefingRating;
  priceTarget: string | null;
  priceTargetCurrency: string | null;
  priceTargetFromAnalysis: boolean;
  recommendedAllocationPct: string | null;
  inPortfolio: boolean;
  note: string;
  /** Chiusura EOD di quel giorno: contesto, non una misura. */
  close: string | null;
};

/**
 * ⚠️ Se e come il prezzo obiettivo si può disegnare, **deciso dal server**: due
 * valute diverse sulla stessa scala producono una linea credibile e falsa, e
 * quella regola non deve poter essere dimenticata dalla pagina.
 */
export type BriefingPriceScale = {
  currency: string | null;
  withCloses: boolean;
  note: string | null;
};

export type BriefingInstrumentHistory = {
  instrument: { id: string; symbol: string | null; name: string; isin: string | null; currency: string | null };
  days: number;
  from: string;
  to: string;
  /** Dal più vecchio al più recente. */
  points: BriefingHistoryPoint[];
  /** I giorni con un digest, giudizio su questo titolo o meno. */
  briefingDays: string[];
  priceScale: BriefingPriceScale;
};

/** Le finestre offerte dalla pagina: le stesse che il backend accetta. */
export const HISTORY_WINDOWS = [7, 30, 90] as const;

export function useInstrumentBriefingHistory(instrumentId: string | undefined, days: number) {
  return useQuery({
    queryKey: [...briefingKeys.all, 'instrument', instrumentId ?? '', days] as const,
    queryFn: ({ signal }) =>
      api.get<BriefingInstrumentHistory>(`/briefings/instrument/${instrumentId}?days=${days}`, signal),
    enabled: Boolean(instrumentId),
  });
}
